import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import numeral from 'numeral';
import { Link } from 'react-router-dom';
import Fa from 'react-fontawesome';
import moment from 'moment';
import { each, delay, find, cloneDeep } from 'lodash';

import ReactHighcharts from 'react-highcharts';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { Element, scroller } from 'react-scroll';
import NumberDisplay from '../../../../components/number-display';
import ProgressBar from '../../../../components/progress-bar';
import SearchForm from '../../../../components/search-form';
import PeriodField from '../../components/period-field/period-field';
import HistoryPeriodField from '../../components/history-period-field/history-period-field';


import { format as formatMoney } from '../../../../components/money-display';
import { getColorFromKey, formatRange } from '../../../../util/helpers';

import ForecastNav from '../../components/nav';
import HistoryDetail from '../../components/history-detail/history-detail';

// Import Actions
import { fetchHistoryOpportunities, resetHistory, reset, fetchForcastPeriods } from '../../ForecastActions';

// Import Selectors
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getSelectedPeriod, getHistoryForecasts, getHistoryLoading, getHistoryLoaded, getHistoryErrors, getHistoryHasErrors, getForcastPeriods } from '../../ForecastReducer';
import styles from './styles.css';


function getDefaultState() {
  const endOfMonth = moment().isSame(moment().endOf('month'), 'day') ? moment().endOf('month') : moment().subtract(1, 'months').endOf('month');

  return {
    period: [endOfMonth.clone().subtract(6 - 1, 'months')
      .startOf('month')
      .format('YYYY-MM-DD'),
    endOfMonth.endOf('month')
      .format('YYYY-MM-DD')],
    selectedPeriod: 'month',
  };
}

class HistoryForecastPage extends Component {
  constructor() {
    super();
    this.state = {
      loadingPeriods: true,
      ...cloneDeep(getDefaultState()),
      start: null,
      end: null,
      query: null,
      team: null,
      submittedPeriod: [],
    };

    this.handleFormChange = this.handleFormChange.bind(this);
    this.handleHistoryPeriodChange = this.handleHistoryPeriodChange.bind(this);
    this.hanldeReset = this.hanldeReset.bind(this);
    this.handleSeriesClick = this.handleSeriesClick.bind(this);
    this.afterRender = this.afterRender.bind(this);
  }

  componentDidMount() {
    this.props.dispatch(reset());
    this.props.dispatch(fetchForcastPeriods({ all: true }))
      .then(() => {
        this.setState({ loadingPeriods: false });
      });
    this.props.dispatch(resetHistory()).then(() => {
      this.mounted = true;
    });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getSectionTitle() {
    const { selectedPeriod } = this.props;
    let period = 'Period';
    if (selectedPeriod === 'week') {
      period = 'Weekly Period';
    } else if (selectedPeriod === 'month') {
      period = 'Monthly Period';
    } else if (selectedPeriod === '60-day') {
      period = '60-Day Period';
    } else if (selectedPeriod === '90-day') {
      period = '90-day Period';
    }
    return `Forecast History by ${period} Chart`;
  }

  getSectionSubTitle() {
    const { submittedPeriod } = this.state;
    if (submittedPeriod.length === 2) {
      return formatRange(submittedPeriod[0], submittedPeriod[1]);
    }
    return null;
  }

  handleFormChange(params) {
    if (this.mounted) {
      const { period, query, team } = params;
      let submittedPeriod = [];
      if (period && period.length === 2) {
        const s = moment(period[0], 'YYYY-MM-DD');
        const e = moment(period[1], 'YYYY-MM-DD');
        if (s.isValid() && e.isValid()) {
          submittedPeriod = [s, e];
        }
      }
      this.setState({
        start: null, end: null, query, submittedPeriod, team,
      });
      this.props.dispatch(reset());
      this.props.dispatch(fetchHistoryOpportunities(params))
        .then(({ total, forecasts }) => {
          const returnResults = forecasts.length;
          if (total && returnResults < total) {
            const el = (
              <div>
                Your search return more than <NumberDisplay value={total} precision={0} /> results.<br />
                For optimal performance, only the latest <NumberDisplay value={returnResults} precision={0} /> will be displayed.
              </div>
            );
            this.context.addNotification(el, { type: 'warning', autoClose: 30 * 1000 });
          }
          if (forecasts && forecasts.length) {
            this.loadDetail(forecasts[0]._id.start, forecasts[0]._id.end);
          }
        });
    }
  }

  handleHistoryPeriodChange(selectedPeriod) {
    this.setState({ selectedPeriod });
  }

  hanldeReset() {
    this.props.dispatch(reset());
    this.setState({
      start: null, end: null, query: null, submittedPeriod: [], ...cloneDeep(getDefaultState()),
    });
    this.props.dispatch(resetHistory());
  }

  scrollToDetail() {
    delay(
      () => {
        scroller.scrollTo('historyDetail', { duration: 500, smooth: true, offset: -70 });
      },
      1000,
    );
  }

  loadDetail(start, end, scroll = false) {
    this.setState({ start, end });
    if (scroll) {
      this.scrollToDetail();
    }
  }

  handleSeriesClick(e) {
    this.loadDetail(e.point.category.start, e.point.category.end, true);
  }

  afterRender(chart) {
    delay(
      () => {
        if (this.mounted && chart.container) {
          const els = chart.container.querySelectorAll('.x-axis-label');
          each(els, (el) => {
            el.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              const start = moment(el.getAttribute('data-start'), 'YYYY-MM-DD');
              const end = moment(el.getAttribute('data-end'), 'YYYY-MM-DD');
              if (start && start.isValid() && end && end.isValid()) {
                this.loadDetail(start, end, true);
              }
            });
          });
        }
      },
      500,
    );
  }

  renderDropdown() {
    const { forecasts } = this.props;
    const { start, end } = this.state;
    const active = find(forecasts, f => f._id.start.isSame(start, 'day') &&
        f._id.end.isSame(end, 'day'));
    return (
      <DropdownButton
        onSelect={(args) => {
          this.loadDetail(...args, true);
        }}
        bsSize="sm"
        title={active ? active.longLabel : 'Period'}
        id="history-period"
      >
        {forecasts.map(period => (
          <MenuItem
            active={active && active._id === period._id}
            key={period.longLabel}
            eventKey={[period._id.start, period._id.end]}
          >
            {period.longLabel}
          </MenuItem>
        ))}
      </DropdownButton>
    );
  }

  renderEmptyMessage() {
    return (
      <div className="alert alert-danger">
        <p><strong><Fa name="exclamation-triangle" /> No records found matching your criteria.</strong></p>
        <p>Make sure your <Link to="/setup">mappings</Link> are setup correctly or broaden your search criteria to see more results.</p>
      </div>
    );
  }

  renderError() {
    const { errors, hasErrors } = this.props;
    if (!hasErrors) {
      return null;
    }
    let message = errors.messge;
    if (errors.data) {
      message = errors.data.message || errors.data;
    }
    return (
      <div className="alert alert-danger">
        <Fa name="exclamation-triangle" /> {message}
      </div>
    );
  }

  renderGraph() {
    const { forecasts, organization } = this.props;
    const selectedStart = this.state.start;
    const selectedEnd = this.state.end;
    const sectionTitle = this.getSectionTitle();
    const subtitle = this.getSectionSubTitle();
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    const categories = [];
    /*
    {
      name: 'Beginning',
      data: [],
      color: '#0c7cd5',
      type: 'line',
      zIndex: 10,
      marker: {
        symbol: 'circle',
      },
    },
    */
    const blueAccuracy = '#0c7cd5';
    const series = [
      {
        name: 'Forecast Accuracy',
        data: [],
        color: blueAccuracy,
        type: 'line',
        zIndex: 10,
        yAxis: 1,
        marker: {
          symbol: 'circle',
        },
        stack: '3',
      },
      {
        name: 'Slipped', data: [], color: '#ccc', stack: '1',
      },
      {
        name: 'Won', data: [], color: getColorFromKey('won'), stack: '1',
      },
      {
        name: 'Lost', data: [], color: getColorFromKey('lost'), stack: '1',
      },
      {
        name: 'No Decision', data: [], color: getColorFromKey('no decision'), stack: '1',
      },
      {
        name: 'Won New', data: [], color: '#b2dbfb', stack: '2',
      },
      {
        name: 'Won Forecast', data: [], color: '#9575CD', stack: '2',
      },

    ];
    const attrs = ['accuracy', 'slipped_amount', 'won_amount', 'lost_amount', 'no_decision_amount', 'new_won_amount', 'closed_won_amount']; // order matter!
    let maxYAxis2 = 100;
    each(forecasts, (forecast) => {
      const { label, longLabel, accuracy } = forecast;
      each(attrs, (attr, idx) => {
        const val = forecast[attr] || 0;
        const formatedY = idx === 0 ? numeral(val).format('0.0%') : formatMoney(val, true, 1, symbol, suffix);
        const y = idx === 0 ? val * 100 : val;
        series[idx].data.push({ y, formatedY });
      });
      const accuracyStr = numeral(accuracy).format('0.0%');
      maxYAxis2 = Math.max(maxYAxis2, accuracy * 100);

      const newWon = forecast.new_won_amount;
      const forecastWon = forecast.won_amount;
      const closedWon = forecast.closed_won_amount;
      const forecastLost = forecast.lost_amount;
      const forecastNoDecision = forecast.no_decision_amount;
      const forecastSlipped = forecast.slipped_amount;
      const totalWon = newWon + closedWon;
      const totalForecast = forecastSlipped + forecastWon + forecastLost + forecastNoDecision;


      categories.push({
        label,
        heading: longLabel,
        accuracy: accuracyStr,
        start: forecast._id.start,
        end: forecast._id.end,
        tooltips: { // stacked tooltips
          stack1: [
            { label: 'Total Forecast', value: formatMoney(totalForecast, true, 1, symbol, suffix), color: '#fff' },
            { label: 'Won', value: formatMoney(forecastWon, true, 1, symbol, suffix), color: getColorFromKey('won') },
            { label: 'Slipped', value: formatMoney(forecastSlipped, true, 1, symbol, suffix), color: '#ccc' },
            { label: 'Lost', value: formatMoney(forecastLost, true, 1, symbol, suffix), color: getColorFromKey('lost') },
            { label: 'No Decision', value: formatMoney(forecastNoDecision, true, 1, symbol, suffix), color: getColorFromKey('noDecision') },
            { label: 'Forecast Accuracy', value: accuracyStr, color: blueAccuracy },
          ],
          stack2: [
            { label: 'Total Won', value: formatMoney(totalWon, true, 1, symbol, suffix), color: getColorFromKey('won') },
            { label: 'Won Forecast', value: formatMoney(closedWon, true, 1, symbol, suffix), color: '#9575CD' },
            { label: 'Won New', value: formatMoney(newWon, true, 1, symbol, suffix), color: '#b2dbfb' },
          ],
          stack3: [
            { label: 'Forecast Accuracy', value: accuracyStr, color: blueAccuracy },
            { label: 'Total Forecast', value: formatMoney(totalForecast, true, 1, symbol, suffix), color: '#fff' },
            { label: 'Total Won', value: formatMoney(totalWon, true, 1, symbol, suffix), color: getColorFromKey('won') },
          ],
        },
      });
    });
    const that = this;
    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        type: 'column',
        animation: {
          duration: 1000,
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories,
        crosshair: true,
        labels: {
          formatter: function fmt() {
            const { label, start, end } = this.value;
            const isActive = (selectedStart && selectedStart.isSame(start, 'day') && selectedEnd && selectedEnd.isSame(end, 'day'));
            return `<a style="${isActive ? 'font-weight:bold;color: #222;' : ''}" href="" class="x-axis-label"
              data-start="${start.format('YYYY-MM-DD')}"
              data-end="${end.format('YYYY-MM-DD')}">${label}</a>`;
          },
          useHTML: true,
          zIndex: 1,
        },
      },
      yAxis: [
        {
          title: {
            text: suffix && suffix.length ? suffix : null,
          },
          labels: {
            formatter: function f() {
              let v = 0;
              if (this.value !== 0) {
                v = formatMoney(this.value, true, 1, symbol);
              }
              return v;
            },
          },
          alternateGridColor: 'rgba(66,66,66,0.05)',
          lineWidth: 0,
          plotLines: [{
            value: 0,
            dashStyle: 'dashed',
            color: 'rgba(0, 0, 0, 0.5)',
            width: 2,
            label: {
              text: null,
            },
          }],
          stackLabels: {
            enabled: true,
            style: {
              fontWeight: 'bold',
            },
            formatter: function ft() {
              return formatMoney(this.total, true, 1, symbol);
            },
          },
        },
        {
          gridLineWidth: 0,
          max: maxYAxis2,
          min: 0,
          allowDecimals: false,
          title: {
            text: 'Forecast Accuracy ',
            style: {
              color: '#0c7cd5',
            },
          },
          labels: {
            formatter: function f() {
              return `${this.value}%`;
            },
            style: {
              color: '#0c7cd5',
            },
          },
          opposite: true,
        },
      ],
      tooltip: {
        formatter: function fmt() {
          const stackSelected = this.point.series.options.stack;
          const categorySelected = this.point.category;
          const { heading } = this.x;
          let str = '';
          const stackKey = `stack${stackSelected}`;
          if (categorySelected && categorySelected.tooltips && categorySelected.tooltips[stackKey]) {
            each(categorySelected.tooltips[stackKey], (point) => {
              str += `<tr>
                <td style="color:${point.color};padding:0">${point.label}:&nbsp;</td>
                <td style="padding:0;padding-left:10px;text-align:right;"><b>${point.value}</b></td>
              </tr>`;
            });
          }
          return `<span style="font-size:10px">${heading}</span><table style="margin-top:8px;width: 100%;">${str}</table>`;
        },
        positioner: function positioner(labelWidth, labelHeight, point) {
          let tooltipX = 0;
          let tooltipY = 0;
          const chart = this.chart;
          const marginX = 20;
          const marginY = 20;
          if (point.plotX + labelWidth > chart.plotWidth) {
            tooltipX = point.plotX + chart.plotLeft - labelWidth - marginX;
          } else {
            tooltipX = point.plotX + chart.plotLeft + marginX;
          }
          tooltipY = point.plotY + chart.plotTop - marginY;
          return {
            x: tooltipX,
            y: tooltipY,
          };
        },
        shared: false,
        useHTML: true,
        backgroundColor: 'rgba(0,0,0, 0.75)',
        borderColor: 'rgba(0,0,0, 0.95)',
        zIndex: 1999,
        style: {
          color: 'rgba(255, 255, 255, 0.75)',
        },
      },
      plotOptions: {
        series: {
          stacking: 'normal',
          marker: {
            fillColor: '#ffffff',
            lineWidth: 2,
            radius: 5,
            lineColor: null, // inherit from series
          },
          events: {
            click: this.handleSeriesClick,
            legendItemClick: function legendItemClick() {
              that.afterRender(this.chart);
            },
          },
        },
      },
      series,
    };
    return (<div className={styles.graphWrapper}>
      <div className="container">
        <div className="animated fadeIn">
          <div>
            <h5 className="page-sub-header">{sectionTitle}</h5>
            {subtitle !== null && <h6 className="sub-header-with-controls">{subtitle}</h6>}
            <ReactHighcharts config={config} ref="forecast-graph" callback={this.afterRender} />
          </div>
        </div>
      </div>
    </div>);
  }

  render() {
    const {
      loading, loaded, hasErrors, forecasts, periods,
    } = this.props;
    const {
      loadingPeriods, selectedPeriod, start, end, query, team,
    } = this.state;
    const total = forecasts.length;

    return (
      <div>
        <Helmet title="Forecast" />
        <div className="container">
          <div className="clearfix">
            <div className="pull-left">
              <ForecastNav />
            </div>
            <div className="pull-right">
              {loadingPeriods && <Fa name="spinner" spin />}
              {!loadingPeriods && <SearchForm
                onSearchChange={this.handleFormChange}
                onReset={this.hanldeReset}
                pullRight
                type="forecast/history"
                disabledRootLogic
                defaultCriteria={{
                  type: 'groupAND',
                  values: [],
                }}
                queryParams={{ forecast: true, selectedPeriod, period: this.state.period }}
                disabledFilters={['stage']}
              >
                <PeriodField
                  periods={periods}
                  path={[]}
                  level={1}
                  showDelete={false}
                  showInclude={false}
                  searchCriteria={{
                    _id: 'forecast-period',
                    type: 'include',
                    filter: {
                      _id: 'forecast-period-filter',
                      label: 'History',
                    },
                  }}
                  values={this.state.period}
                  onChange={(values) => { this.setState({ period: values }); }}
                />
                <HistoryPeriodField
                  disabled={loading}
                  path={[]}
                  level={1}
                  showDelete={false}
                  showInclude={false}
                  searchCriteria={{
                    _id: 'forecast-period-range',
                    type: 'include',
                    filter: {
                      _id: 'forecast-period-range-filter',
                      label: 'Period',
                    },
                  }}
                  value={this.state.selectedPeriod}
                  onChange={this.handleHistoryPeriodChange}
                />
              </SearchForm>}
            </div>
          </div>
          <p />
          {!loaded && <div className={`${(loading ? '' : 'invisible')}`}><ProgressBar /></div>}
          {loaded && total === 0 && this.renderEmptyMessage()}
        </div>
        {loaded && total > 0 && <div>
          {this.renderGraph()}
          <Element name="historyDetail" />
          <HistoryDetail start={start} end={end} query={query} team={team} dropdown={this.renderDropdown()} />
        </div>}
        <div className="container">
          {hasErrors && this.renderError()}
        </div>
      </div>
    );
  }
}


// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    forecasts: getHistoryForecasts(state),
    loading: getHistoryLoading(state),
    loaded: getHistoryLoaded(state),
    errors: getHistoryErrors(state),
    hasErrors: getHistoryHasErrors(state),
    periods: getForcastPeriods(state),
    organization: getUserCurrentOrganization(state),
    selectedPeriod: getSelectedPeriod(state),
  };
}

HistoryForecastPage.propTypes = {
  forecasts: PropTypes.array.isRequired,
  periods: PropTypes.array.isRequired,
  selectedPeriod: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  errors: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  hasErrors: PropTypes.bool.isRequired,
  organization: PropTypes.object,
};

HistoryForecastPage.contextTypes = {
  router: PropTypes.object,
  addNotification: PropTypes.func.isRequired,
};
export default connect(mapStateToProps)(HistoryForecastPage);
